// Designs B and D of the New Enquiry form (Mohammed 18/10): project + phase, the job (new, or the one the project + phase
// already has), the customer quote (bidForm) and one Save that stores the job first when it is new. The component gives
// the template.
import api from './sharedEnquiryApi'
import bidForm from './bidForm'
import { missingMandatory, toDmy, errorText } from './sharedEnquiryLogic'
import { addEnquiryLevel, canAddEnquiry } from './openNewEnquiry'
import { viewOf } from './enquiryLayout'

export default {
  mixins: [bidForm],
  props: {
    customerId0: { type: Number, default: null },
    custName0: { type: String, default: '' },
    // "New Customer Quote" on a job: the job is fixed
    sharedEnqId0: { type: Number, default: null }
  },
  data() {
    return {
      project: null, phaseId: null, sharedEnqId: null, existingBidCount: 0, job: { dateReceived: toDmy(new Date()) },
      settings: [], projectSearch: false, phaseDialog: false, newPhase: { phaseNo: '', description: '' }, busy: false, savingJob: false
    }
  },
  computed: {
    // the review menu's ?view=, carried on to the job page so the same layout is compared
    viewQuery() {
      const view = viewOf(this.$route && this.$route.query)
      return view ? { view } : {}
    },
    // the duplicate question in a modal over the form, not a table in it (Mohammed 18/10)
    askUpFront() { return true },
    permissionLevel() { return addEnquiryLevel(this.$store) },
    allowed() { return canAddEnquiry(this.permissionLevel) },
    onlyUserBranch() { return this.permissionLevel === 5 },
    initialCustomer() { return this.customerId0 ? { customerId: this.customerId0, custName: this.custName0 } : null },
    fixedJob() { return !!this.sharedEnqId0 },
    phaseOptions() {
      const phases = (this.project && this.project.phases) || []
      return [{ phaseId: null, label: 'Main project' }]
        .concat(phases.map(ph => ({ phaseId: ph.phaseId, label: 'Phase ' + ph.phaseNo + (ph.description ? ': ' + ph.description : '') })))
    },
    jobMissing() {
      if (this.sharedEnqId) return []
      return missingMandatory(this.settings.filter(s => s.level !== 'bid'), { project: this.project, job: this.job })
    },
    allMissing() { return this.jobMissing.concat(this.missing) }
  },
  watch: {
    // the job on the chosen project + phase, if the CRM has it already: the customer is checked against it
    phaseId() { if (!this.fixedJob) this.useExistingJob() },
    sharedEnqId() { if (!this.savingJob) this.runCheck() }
  },
  async created() {
    try {
      this.settings = (await api.fieldSettings()) || []
    } catch (e) {
      this.error = errorText(e, 'Could not load which fields are mandatory')
    }
    if (this.sharedEnqId0) await this.loadJob(this.sharedEnqId0)
  },
  methods: {
    close() { this.$router.back() },
    onProject(p) {
      this.project = { phases: [], sharedEnquiries: [], ...p }
      this.projectSearch = false
      this.phaseId = null
      this.useExistingJob()
    },
    onExistingEnquiry({ project, sharedEnq }) {
      this.project = { phases: [], ...project }
      this.projectSearch = false
      this.phaseId = sharedEnq.phaseId || null
      this.loadJob(sharedEnq.sharedEnqId)
    },
    useExistingJob() {
      const e = ((this.project && this.project.sharedEnquiries) || []).find(s => (s.phaseId || null) === this.phaseId)
      if (e) {
        this.loadJob(e.sharedEnqId)
      } else {
        this.sharedEnqId = null
        this.existingBidCount = 0
        this.job = { dateReceived: toDmy(new Date()) }
      }
    },
    async loadJob(id) {
      try {
        const s = await api.sharedEnquiry(id)
        const { project, phase, bids, values, attachments, ...job } = s
        if (!this.project) this.project = { phases: phase ? [phase] : [], ...project }
        if (this.fixedJob) this.phaseId = s.phaseId || null
        this.job = job
        this.existingBidCount = (bids || []).length
        this.sharedEnqId = id
      } catch (e) {
        this.error = errorText(e, 'Could not load the enquiry')
      }
    },
    async addPhase() {
      this.busy = true
      try {
        const ph = await api.addPhase(this.project.projectId, { phaseNo: this.newPhase.phaseNo.toUpperCase(), description: this.newPhase.description })
        this.project.phases = (this.project.phases || []).concat([ph])
        this.phaseId = ph.phaseId
        this.newPhase = { phaseNo: '', description: '' }
        this.phaseDialog = false
      } catch (e) {
        this.error = errorText(e, 'Could not add the phase')
      } finally {
        this.busy = false
      }
    },
    // One Save, as today: the job first when it is new (with its attachments), then the customer quote
    async save() {
      this.submitted = true
      this.error = ''
      if (!this.project || this.allMissing.length || this.blocked) return
      if (!this.sharedEnqId) {
        this.savingJob = true
        try {
          const s = await api.createSharedEnquiry({ projectId: this.project.projectId, phaseId: this.phaseId, ...this.job })
          this.sharedEnqId = s.sharedEnqId
          // design D shows the attachments on a tab, which may never have been opened
          const ok = this.$refs.attachments ? await this.$refs.attachments.uploadQueued(s.sharedEnqId) : true
          if (!ok) this.error = 'The enquiry is saved, but some attachments did not upload. Add them again on the enquiry page.'
        } catch (e) {
          this.error = errorText(e, 'Could not save the enquiry details')
          return
        } finally {
          this.savingJob = false
        }
      }
      await this.add()
    },
    bidAdded(opp) {
      this.$toast && this.$toast.add({ severity: 'success', summary: 'Quotation added', detail: (opp.custName || '') + ' ' + (opp.sQuoteNo || ''), life: 4000 })
      this.$router.push({ name: 'sharedEnquiry', params: { id: this.sharedEnqId }, query: this.viewQuery })
    }
  }
}
